/**
 * La trousse de la feuille de brouillon (migration 0735) : un accessoire de COLLECTION.
 *
 * Trente-deux couvertures, chacune tirée en un nombre d'exemplaires figé en puissances de 2 — la
 * trousse de base 2^31, la plus rare un seul exemplaire au monde. Qui l'obtient reçoit son NUMÉRO
 * d'exemplaire : c'est ce qui en fait un objet qu'on possède.
 *
 * Au départ tout le monde a la trousse de base : `ensure_trousse` la donne au premier appel.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

export interface Trousse {
  slug: string;
  name: string;
  /** 0 = la plus commune (la trousse de base), 31 = la plus rare */
  rank: number;
  copies: number;
  image_path: string;
  /** le numéro d'exemplaire de SON exemplaire */
  serial: number;
}

export interface OwnedTrousse extends Trousse {
  source: 'default' | 'reward' | 'gift' | 'admin' | 'trade';
  equipped: boolean;
  acquired_at: string;
  /** combien d'exemplaires de ce modèle (migration 0739) */
  count: number;
}

export interface TrousseState {
  equipped: Trousse | null;
  owned: OwnedTrousse[];
}

/** La couverture servie par l'app (`public/trousses/NN.webp`). */
export function trousseImage(imagePath: string): string {
  return `/${imagePath.replace(/^\/+/, '')}`;
}

export const TROUSSE_RARITIES = [
  'commune',
  'peu commune',
  'rare',
  'très rare',
  'légendaire',
  'unique',
] as const;
export type TrousseRarity = (typeof TROUSSE_RARITIES)[number];

/**
 * Le palier de rareté se LIT sur le nombre d'exemplaires — rien à stocker : 1 exemplaire = unique,
 * et chaque palier couvre quelques puissances de 2. Sur les 32 couvertures : 9 communes, 6 peu
 * communes, 6 rares, 6 très rares, 4 légendaires, 1 unique.
 */
export function trousseRarity(copies: number): TrousseRarity {
  if (copies <= 1) return 'unique';
  if (copies <= 16) return 'légendaire';
  if (copies <= 1024) return 'très rare';
  if (copies <= 65536) return 'rare';
  if (copies <= 4194304) return 'peu commune';
  return 'commune';
}

/** La teinte de l'aura d'une trousse selon sa rareté : argent, vert, bleu, violet, or, rose. */
export const TROUSSE_RARITY_COLORS: Record<TrousseRarity, string> = {
  commune: '#a9bde3',
  'peu commune': '#4ade80',
  rare: '#4f9dff',
  'très rare': '#b67cff',
  légendaire: '#f5b82e',
  unique: '#ff4d8d',
};

export function trousseRarityColor(copies: number): string {
  return TROUSSE_RARITY_COLORS[trousseRarity(copies)];
}

/**
 * La puissance de l'aura (0..100, échelle de `AvatarAura`) : elle grandit avec le rang, donc avec la
 * rareté — une commune a un halo minime, l'exemplaire unique tout l'orage. PUR.
 */
export function trousseAuraPower(rank: number): number {
  const r = Math.max(0, Math.min(31, rank));
  // courbe qui démarre lentement : les communes n'ont qu'un léger halo, l'unique tout l'orage
  return Math.round(4 + Math.pow(r / 31, 1.4) * 96);
}

/** « 2 147 483 648 » — groupes de trois, espace insécable fine comme ailleurs dans l'app. */
export function formatCopies(n: number): string {
  return Math.max(0, Math.round(n))
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

/** « n° 42 sur 1 024 », ou « exemplaire unique » quand il n'en existe qu'un. */
export function serialLabel(serial: number, copies: number): string {
  if (copies <= 1) return 'exemplaire unique';
  return `n° ${formatCopies(serial)} sur ${formatCopies(copies)}`;
}

export function trousseErrorMessage(code: string): string {
  if (code.includes('trousse_sold_out'))
    return 'Cette trousse est épuisée : tous ses exemplaires sont déjà pris.';
  if (code.includes('trousse_not_owned')) return 'Tu n’as pas encore cette trousse.';
  if (code.includes('trousse_not_found')) return 'Cette trousse n’existe pas.';
  if (code.includes('not_authenticated')) return 'Connecte-toi pour avoir ta trousse.';
  return 'La trousse n’a pas pu être changée. Réessaie.';
}

/** Ce que la base renvoie, relu sans faire confiance (une colonne peut manquer sur un vieux client). */
function readState(data: unknown): TrousseState {
  const o = (data ?? {}) as { equipped?: unknown; owned?: unknown };
  const one = (v: unknown): OwnedTrousse | null => {
    const t = v as Partial<OwnedTrousse> | null;
    if (!t || typeof t.slug !== 'string' || typeof t.image_path !== 'string') return null;
    return {
      slug: t.slug,
      name: typeof t.name === 'string' ? t.name : t.slug,
      rank: Number(t.rank ?? 0),
      copies: Number(t.copies ?? 1),
      image_path: t.image_path,
      serial: Number(t.serial ?? 1),
      source: (t.source ?? 'default') as OwnedTrousse['source'],
      equipped: !!t.equipped,
      acquired_at: typeof t.acquired_at === 'string' ? t.acquired_at : '',
      count: Math.max(1, Number(t.count ?? 1)),
    };
  };
  return {
    equipped: one(o.equipped),
    owned: (Array.isArray(o.owned) ? o.owned : []).map(one).filter((t): t is OwnedTrousse => !!t),
  };
}

export const myTrousseKey = (userId: string) => ['my-trousse', userId] as const;

/** Sa trousse : donnée au premier appel, gardée ensuite. Jamais pour une session anonyme. */
export function useMyTrousse(userId: string | null | undefined) {
  return useQuery({
    queryKey: myTrousseKey(userId ?? ''),
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<TrousseState> => {
      const { data, error } = await db.rpc('ensure_trousse');
      if (error) throw new Error(error.message);
      return readState(data);
    },
  });
}

/** On change de trousse comme de coque — seulement parmi celles qu'on possède. */
export function useEquipTrousse(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string): Promise<TrousseState> => {
      const { data, error } = await db.rpc('equip_trousse', { p_slug: slug });
      if (error) throw new Error(error.message);
      return readState(data);
    },
    onSuccess: (state) => qc.setQueryData(myTrousseKey(userId), state),
  });
}

// ── La collection : roue du jour et cadeaux (migration 0738) ────────────────

/** Une des 32 couvertures, vue depuis le compte : possédée, portée, offerte en attente. */
export interface CollectionTrousse {
  slug: string;
  name: string;
  rank: number;
  copies: number;
  /** exemplaires déjà attribués dans le monde */
  taken: number;
  image_path: string;
  is_default: boolean;
  owned: boolean;
  /** combien d'exemplaires on en a (0 = pas dans la collection) */
  count: number;
  /** les numéros de ses exemplaires, du plus petit au plus grand */
  serials: number[];
  /** l'exemplaire porté, sinon le plus petit numéro (aussi celui d'une trousse offerte en attente) */
  serial: number | null;
  source: OwnedTrousse['source'] | null;
  equipped: boolean;
  /** offerte, personne ne l'a encore prise : le lien */
  gift_token: string | null;
}

export interface WheelState {
  spun_today: boolean;
  /** la trousse gagnée aujourd'hui, null = perdu ou pas encore tourné */
  won: Pick<Trousse, 'slug' | 'name' | 'rank' | 'copies' | 'image_path'> | null;
  /** un exercice terminé aujourd'hui (jour de Paris) : c'est ce qui donne droit au tour (0741) */
  exercise_today: boolean;
  /** pas encore tourné ET un exercice terminé aujourd'hui */
  can_spin: boolean;
  /** jours perdus d'affilée juste avant aujourd'hui */
  streak: number;
  /** la chance du jour : 1/10 + 1/10 par jour perdu d'affilée (10/10 au plus) */
  chance: number;
  next_spin_at: string;
}

export interface TrousseCollection {
  catalog: CollectionTrousse[];
  win_chance: number;
  /** combien d'exemplaires on rend pour 1 exemplaire du modèle au-dessus */
  trade_cost: number;
  spin: WheelState;
}

export interface SpinResult {
  won: boolean;
  /** `count` = combien d'exemplaires de ce modèle on a maintenant */
  trousse: (Trousse & { count: number }) | null;
  nothing_left: boolean;
  next_spin_at: string;
}

/** Les liens partagés ont une origine FIXE (jamais `capacitor://` dans l'app native). */
export const TROUSSE_PUBLIC_ORIGIN = 'https://app.jeraisonne.fr';

const GIFT_TOKEN = /^[0-9a-f]{16}$/;

/** Le jeton d'un lien de cadeau (16 caractères hexadécimaux), ou null. */
export function normalizeTrousseToken(raw: string): string | null {
  const t = raw.trim().toLowerCase();
  return GIFT_TOKEN.test(t) ? t : null;
}

export function trousseGiftUrl(token: string): string {
  return `${TROUSSE_PUBLIC_ORIGIN}/trousse/${token}`;
}

export function trousseGiftShareText(name: string): string {
  return `Je t’offre ma trousse « ${name} » sur JeRaisonne ! Ouvre le lien pour l’ajouter à ta collection :`;
}

export function trousseGiftErrorMessage(code: string): string {
  if (code.includes('gift_not_found')) return 'Ce lien ne mène à aucune trousse.';
  if (code.includes('gift_already_claimed')) return 'Quelqu’un a déjà pris cette trousse.';
  if (code.includes('own_gift'))
    return 'C’est ta trousse : partage le lien à quelqu’un d’autre, ou reprends-la dans ta collection.';
  if (code.includes('already_owned')) return 'Tu as déjà cette trousse dans ta collection.';
  if (code.includes('trousse_base_not_giftable'))
    return 'La trousse de base ne s’offre pas : tout le monde l’a déjà.';
  if (code.includes('already_spun')) return 'Tu as déjà tourné la roue aujourd’hui.';
  if (code.includes('no_exercise_today'))
    return 'Termine un exercice aujourd’hui pour tourner la roue.';
  if (code.includes('trade_not_enough'))
    return 'Il te faut 3 exemplaires de cette trousse pour l’échanger.';
  if (code.includes('trade_top')) return 'C’est la trousse la plus rare : rien au-dessus.';
  return trousseErrorMessage(code);
}

/** Combien de couvertures on a, sur combien (une trousse offerte en attente ne compte pas). */
export function collectionCount(catalog: CollectionTrousse[]): { owned: number; total: number } {
  return { owned: catalog.filter((t) => t.owned).length, total: catalog.length };
}

/** Le nombre d'exemplaires rendus dans un échange (miroir de `trade_trousses`, migration 0739). */
export const TRADE_COST = 3;

export type TradeOption =
  | { kind: 'none' }
  /** il en manque : « encore 1 exemplaire » */
  | { kind: 'missing'; missing: number; target: CollectionTrousse }
  /** plus d'exemplaire libre au-dessus */
  | { kind: 'sold_out'; target: CollectionTrousse }
  | { kind: 'ready'; target: CollectionTrousse };

/**
 * Ce qu'on peut faire de ses exemplaires d'un modèle : l'échanger (3 → 1 du modèle au rang suivant),
 * ou rien. Rien à proposer si on n'en a aucun, ni pour la plus rare. PUR — le serveur revérifie.
 */
export function tradeOption(
  catalog: CollectionTrousse[],
  t: CollectionTrousse,
  cost = TRADE_COST,
): TradeOption {
  const target = catalog.find((c) => c.rank === t.rank + 1);
  // la trousse de base n'existe qu'en un exemplaire par compte : jamais d'échange
  if (!target || t.count < 1 || t.is_default) return { kind: 'none' };
  if (target.taken >= target.copies) return { kind: 'sold_out', target };
  if (t.count < cost) return { kind: 'missing', missing: cost - t.count, target };
  return { kind: 'ready', target };
}

/** « n° 1, 4 et 12 » : les numéros de ses exemplaires (les 6 premiers, puis « … »). */
export function serialsLabel(serials: number[]): string {
  const shown = serials.slice(0, 6).map(formatCopies);
  if (shown.length === 0) return '';
  const more = serials.length > 6 ? ' …' : '';
  if (shown.length === 1) return `n° ${shown[0]}`;
  return `n° ${shown.slice(0, -1).join(', ')} et ${shown[shown.length - 1]}${more}`;
}

/** Sur combien de cases dorées (sur 10) on peut s'arrêter aujourd'hui : 1 à 10. */
export function goldSlices(chance: number): number {
  return Math.max(1, Math.min(WHEEL_SLICES, Math.round(chance * WHEEL_SLICES)));
}

/** « 1 chance sur 10 », « 3 chances sur 10 » : la chance de gagner, en mots. */
export function winChanceLabel(chance: number): string {
  if (!(chance > 0)) return 'aucune chance';
  const n = goldSlices(chance);
  return `${n} chance${n > 1 ? 's' : ''} sur ${WHEEL_SLICES}`;
}

/** Ce que la case de la roue dit d'elle-même. PUR. */
export function wheelStatusLabel(spin: WheelState, now = Date.now()): string {
  if (spin.spun_today) return `Revient ${nextSpinLabel(spin.next_spin_at, now)}`;
  if (!spin.exercise_today) return 'Termine un exercice pour la tourner';
  return `Tourne-la ! ${goldSlices(spin.chance)}/${WHEEL_SLICES}`;
}

/**
 * Quand la roue revient : « dans 5 h 12 min », « dans 8 min », « maintenant ». PUR (l'heure est passée).
 */
export function nextSpinLabel(nextSpinAt: string, now = Date.now()): string {
  const ms = Date.parse(nextSpinAt) - now;
  if (!Number.isFinite(ms) || ms <= 0) return 'maintenant';
  const min = Math.ceil(ms / 60_000);
  const h = Math.floor(min / 60);
  const m = min % 60;
  if (!h) return `dans ${m} min`;
  return m ? `dans ${h} h ${String(m).padStart(2, '0')} min` : `dans ${h} h`;
}

/** Le nombre de cases de la roue : les cases dorées (gagné) sont en tête, autant que de chances sur 10. */
export const WHEEL_SLICES = 10;

/**
 * Où la roue s'arrête (degrés, sens des aiguilles d'une montre), le pointeur étant en haut. La case 0
 * est la case dorée : gagné → on s'arrête dessus, perdu → sur une autre case (choisie par `pick` ∈ [0,1[).
 * La roue fait d'abord `turns` tours complets ; un petit décalage dans la case fait plus naturel. PUR.
 */
export function wheelStopAngle(
  won: boolean,
  pick: number,
  {
    turns = 5,
    slices = WHEEL_SLICES,
    gold = 1,
  }: { turns?: number; slices?: number; gold?: number } = {},
): number {
  const size = 360 / slices;
  const g = Math.max(1, Math.min(slices - 1, gold));
  const p = Math.max(0, Math.min(0.999, pick));
  // cases 0 … g − 1 dorées, les autres perdues (10/10 : toutes dorées, on gagne forcément)
  const slice = won
    ? Math.floor(p * Math.min(gold, slices))
    : g + Math.min(slices - g - 1, Math.floor(p * (slices - g)));
  // la case `slice` est centrée en slice × size : il faut la ramener sous le pointeur (0°)
  const jitter = (Math.max(0, Math.min(0.999, pick)) - 0.5) * size * 0.5;
  return turns * 360 + ((360 - slice * size) % 360) + jitter;
}

function readCollection(data: unknown): TrousseCollection {
  const o = (data ?? {}) as {
    catalog?: unknown;
    win_chance?: unknown;
    trade_cost?: unknown;
    spin?: unknown;
  };
  const catalog = (Array.isArray(o.catalog) ? o.catalog : [])
    .map((v): CollectionTrousse | null => {
      const t = v as Partial<CollectionTrousse> | null;
      if (!t || typeof t.slug !== 'string' || typeof t.image_path !== 'string') return null;
      return {
        slug: t.slug,
        name: typeof t.name === 'string' ? t.name : t.slug,
        rank: Number(t.rank ?? 0),
        copies: Number(t.copies ?? 1),
        taken: Number(t.taken ?? 0),
        image_path: t.image_path,
        is_default: !!t.is_default,
        owned: !!t.owned,
        count: Number(t.count ?? (t.owned ? 1 : 0)),
        serials: Array.isArray(t.serials)
          ? t.serials.map(Number)
          : t.owned && t.serial != null
            ? [Number(t.serial)]
            : [],
        serial: t.serial == null ? null : Number(t.serial),
        source: (t.source ?? null) as CollectionTrousse['source'],
        equipped: !!t.equipped,
        gift_token: typeof t.gift_token === 'string' ? t.gift_token : null,
      };
    })
    .filter((t): t is CollectionTrousse => !!t)
    .sort((a, b) => a.rank - b.rank);
  const s = (o.spin ?? {}) as Partial<WheelState>;
  const won = s.won as WheelState['won'] | undefined;
  return {
    catalog,
    win_chance: typeof o.win_chance === 'number' ? o.win_chance : 0.1,
    trade_cost: typeof o.trade_cost === 'number' ? o.trade_cost : TRADE_COST,
    spin: {
      spun_today: !!s.spun_today,
      won: won && typeof won.slug === 'string' ? won : null,
      exercise_today: s.exercise_today !== false,
      can_spin: s.can_spin ?? !s.spun_today,
      streak: Number(s.streak ?? 0),
      chance: Number(s.chance ?? o.win_chance ?? 0.1),
      next_spin_at: typeof s.next_spin_at === 'string' ? s.next_spin_at : '',
    },
  };
}

export const trousseCollectionKey = (userId: string) => ['trousse-collection', userId] as const;

export function useTrousseCollection(userId: string | null | undefined) {
  return useQuery({
    queryKey: trousseCollectionKey(userId ?? ''),
    enabled: !!userId,
    queryFn: async (): Promise<TrousseCollection> => {
      const { data, error } = await db.rpc('get_trousse_collection');
      if (error) throw new Error(error.message);
      return readCollection(data);
    },
  });
}

/** Après un geste sur la collection : la page ET la trousse de l'exercice se rechargent. */
function refreshAll(qc: ReturnType<typeof useQueryClient>, userId: string) {
  void qc.invalidateQueries({ queryKey: trousseCollectionKey(userId) });
  void qc.invalidateQueries({ queryKey: trousseWheelKey(userId) });
  void qc.invalidateQueries({ queryKey: myTrousseKey(userId) });
}

export const trousseWheelKey = (userId: string) => ['trousse-wheel', userId] as const;

/** L'état de la roue seul (fin d'exercice, tableau de bord) — sans le catalogue. */
export function useTrousseWheelState(userId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: trousseWheelKey(userId ?? ''),
    enabled: !!userId && enabled,
    queryFn: async (): Promise<WheelState> => {
      const { data, error } = await db.rpc('get_trousse_wheel_state');
      if (error) throw new Error(error.message);
      return readCollection({ spin: data }).spin;
    },
  });
}

export async function spinTrousseWheel(): Promise<SpinResult> {
  const { data, error } = await db.rpc('spin_trousse_wheel');
  if (error) throw new Error(error.message);
  const o = (data ?? {}) as {
    won?: unknown;
    trousse?: unknown;
    nothing_left?: unknown;
    next_spin_at?: unknown;
  };
  const t = o.trousse as (Partial<Trousse> & { count?: number }) | null | undefined;
  return {
    won: !!o.won,
    trousse:
      t && typeof t.slug === 'string'
        ? {
            slug: t.slug,
            name: typeof t.name === 'string' ? t.name : t.slug,
            rank: Number(t.rank ?? 0),
            copies: Number(t.copies ?? 1),
            image_path: String(t.image_path ?? DEFAULT_IMAGE),
            serial: Number(t.serial ?? 1),
            count: Math.max(1, Number(t.count ?? 1)),
          }
        : null,
    nothing_left: !!o.nothing_left,
    next_spin_at: typeof o.next_spin_at === 'string' ? o.next_spin_at : '',
  };
}

const DEFAULT_IMAGE = 'trousses/01.webp';

/** Porter une trousse depuis la collection (même RPC que la trousse de l'exercice). */
export function useEquipFromCollection(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string) => {
      const { error } = await db.rpc('equip_trousse', { p_slug: slug });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => refreshAll(qc, userId),
  });
}

/** Échanger 3 exemplaires d'un modèle contre 1 du modèle au-dessus. Renvoie la trousse reçue. */
export function useTradeTrousses(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string): Promise<Trousse> => {
      const { data, error } = await db.rpc('trade_trousses', { p_slug: slug });
      if (error) throw new Error(error.message);
      const t = ((data ?? {}) as { trousse?: Partial<Trousse> }).trousse ?? {};
      return {
        slug: String(t.slug ?? ''),
        name: typeof t.name === 'string' ? t.name : String(t.slug ?? ''),
        rank: Number(t.rank ?? 0),
        copies: Number(t.copies ?? 1),
        image_path: String(t.image_path ?? DEFAULT_IMAGE),
        serial: Number(t.serial ?? 1),
      };
    },
    onSuccess: () => refreshAll(qc, userId),
  });
}

/** Offrir une trousse : un exemplaire quitte la collection et attend dans un lien. Renvoie le jeton. */
export function useCreateTrousseGift(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (slug: string): Promise<string> => {
      const { data, error } = await db.rpc('create_trousse_gift', { p_slug: slug });
      if (error) throw new Error(error.message);
      const token = (data as { token?: unknown } | null)?.token;
      if (typeof token !== 'string') throw new Error('gift_not_found');
      return token;
    },
    onSuccess: () => refreshAll(qc, userId),
  });
}

/** Reprendre une trousse offerte que personne n'a prise. */
export function useCancelTrousseGift(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) => {
      const { error } = await db.rpc('cancel_trousse_gift', { p_token: token });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => refreshAll(qc, userId),
  });
}

export interface PublicTrousseGift {
  status: 'pending' | 'claimed' | 'not_found';
  slug?: string;
  name?: string;
  rank?: number;
  copies?: number;
  image_path?: string;
  serial?: number;
  from_first_name?: string | null;
  mine?: boolean;
  own_gift?: boolean;
  /** toujours faux depuis la migration 0739 (gardé pour les anciennes réponses) */
  already_owned?: boolean;
  /** combien d'exemplaires de ce modèle on a déjà */
  owned_count?: number;
}

/** Ce que montre le lien d'une trousse offerte (public, sans compte). */
export function usePublicTrousseGift(token: string | null, userId: string | null | undefined) {
  return useQuery({
    queryKey: ['trousse-gift', token, userId ?? ''],
    enabled: !!token,
    queryFn: async (): Promise<PublicTrousseGift> => {
      const { data, error } = await db.rpc('get_trousse_gift', { p_token: token });
      if (error) throw new Error(error.message);
      const o = (data ?? {}) as PublicTrousseGift;
      return o.status === 'pending' || o.status === 'claimed' ? o : { status: 'not_found' };
    },
  });
}

export async function claimTrousseGift(token: string): Promise<{ slug: string; serial: number }> {
  const { data, error } = await db.rpc('claim_trousse_gift', { p_token: token });
  if (error) throw new Error(error.message);
  const o = (data ?? {}) as { slug?: string; serial?: number };
  return { slug: String(o.slug ?? ''), serial: Number(o.serial ?? 1) };
}
